import React, { useState } from 'react';
import { ExternalLink, ZoomIn, ZoomOut, X, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { fabricIdentifier } from '../../../lib/fabricLabels';
import { StockDot } from '../../fabric/StockDot';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { resolveImageUrl } from '../../../lib/imageCache';
import { brandLabel } from '../../../lib/brandNames';

// Full-screen view of the original swatch file at native resolution (no cropping), with 1x/2x magnifier.
export const FabricLightbox = ({ entry, open, onOpenChange }) => {
  const [zoomed, setZoomed] = useState(false);
  if (!entry) return null;
  const shareSwatch = async () => {
    // Share the preview URL so chat/social apps render a rich card (OG tags); it redirects humans to the gallery.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/fabric/${entry.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link to "${entry.name}" copied`);
    } catch {
      toast.info(url);
    }
  };
  const src = resolveImageUrl(entry.image_url || entry.room_visualization_url, 2048);
  const link = entry.collection_website_url || entry.brand_website;

  return (
    <Dialog open={open} onOpenChange={(v) => { setZoomed(false); onOpenChange(v); }}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] p-0 gap-0 bg-neutral-950 border-neutral-800 text-white flex flex-col" data-testid="fabric-lightbox">
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-neutral-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-6 h-6 rounded-full border border-white/40 shrink-0" style={{ backgroundColor: entry.hex_color || '#ccc' }} />
            <DialogTitle className="font-serif text-lg truncate" data-testid="fabric-lightbox-name">{entry.name}</DialogTitle>
            <span className="text-xs text-neutral-400 truncate hidden sm:inline" data-testid="fabric-lightbox-path">{fabricIdentifier(entry)}</span>
            <StockDot status={entry.stock_status} discontinued={entry.discontinued} size="lg" testId="fabric-lightbox-stock" side="bottom" />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {link && (
              <Button asChild variant="outline" size="sm" className="gap-1.5 border-neutral-700 bg-transparent text-white hover:bg-neutral-800 hover:text-white">
                <a href={link} target="_blank" rel="noopener noreferrer" data-testid="fabric-lightbox-brand-link">
                  <ExternalLink className="w-3.5 h-3.5" /> {entry.collection_website_url ? `${entry.collection_name} on ${brandLabel(entry.brand_name)}` : `${brandLabel(entry.brand_name)} website`}
                </a>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={shareSwatch} className="gap-1.5 border-neutral-700 bg-transparent text-white hover:bg-neutral-800 hover:text-white" data-testid="fabric-lightbox-share-btn" title="Copy a link that opens this swatch">
              <Link2 className="w-4 h-4" /> Share
            </Button>
            <Button variant="outline" size="sm" onClick={() => setZoomed((z) => !z)} className="gap-1.5 border-neutral-700 bg-transparent text-white hover:bg-neutral-800 hover:text-white" data-testid="fabric-lightbox-zoom-btn">
              {zoomed ? <ZoomOut className="w-3.5 h-3.5" /> : <ZoomIn className="w-3.5 h-3.5" />} {zoomed ? 'Fit' : '2× detail'}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="text-white hover:bg-neutral-800 hover:text-white" data-testid="fabric-lightbox-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className={`flex-1 min-h-0 ${zoomed ? 'overflow-auto cursor-zoom-out' : 'overflow-hidden flex items-center justify-center cursor-zoom-in'}`} onClick={() => setZoomed((z) => !z)}>
          {src ? (
            <img
              src={src}
              alt={entry.name}
              data-testid="fabric-lightbox-image"
              className={zoomed ? 'max-w-none w-[200%] sm:w-[160%] h-auto' : 'w-auto h-full object-contain'}
              style={{ imageRendering: 'auto' }}
              draggable={false}
            />
          ) : (
            <div className="w-1/2 aspect-square rounded-xl" style={{ backgroundColor: entry.hex_color || '#ccc' }} />
          )}
        </div>
        <p className="px-5 py-2 text-[11px] text-neutral-500 border-t border-neutral-800">
          Original swatch file shown unedited — click the image to toggle 2× magnification. Screen colors are approximate; request a physical sample for exact matching.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default FabricLightbox;
